import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, formatApiError, useAuth } from "@/lib/auth";
import FinanceLayout, { fmtUsd } from "./FinanceLayout";
import SmartAllocationCard from "./SmartAllocationCard";
import { Loader2, Plus, PieChart, DollarSign, Pencil, Archive, ArchiveRestore, Trash2, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const DistributionsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Live allocation totals broadcast from SmartAllocationCard so we can
  // compute the effective Net Distributable in real-time.
  const [allocated, setAllocated] = useState({ operating: 0, growth: 0, reserve: 0, total: 0 });
  // record paid form
  const [selectedPartner, setSelectedPartner] = useState("");
  const [payAmount, setPayAmount] = useState("");
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  // edit modal
  const [editing, setEditing] = useState(null);
  const [e_amount, setEAmount] = useState("");
  const [e_paid_at, setEPaidAt] = useState("");
  const [e_memo, setEMemo] = useState("");

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (showArchived) params.set("include_archived", "true");
      const [s, h] = await Promise.all([
        apiClient.get("/finance/distributions/summary"),
        apiClient.get(`/finance/distributions?${params.toString()}`),
      ]);
      setSummary(s.data);
      setHistory(h.data.items);
      if (s.data.partners?.[0]) setSelectedPartner((prev) => prev || s.data.partners[0].email);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [showArchived]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh whenever the partner returns to this tab. Guarantees
  // any category change made on the Expenses board (potentially in
  // another tab or by another partner) flows into the Distributions
  // math the moment we come back to look at it.
  useEffect(() => {
    const onFocus = () => load();
    const onVisibility = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [load]);

  const recordPaid = async (e) => {
    e.preventDefault();
    if (!user?.is_partner) return toast.error("Only Partners can record distributions");
    if (!payAmount || !selectedPartner) return toast.error("Partner and amount required");
    setBusy(true);
    try {
      await apiClient.post("/finance/distributions/paid", {
        partner_email: selectedPartner,
        amount: Number(payAmount),
        paid_at: payDate,
        note,
      });
      toast.success("Distribution recorded");
      setPayAmount(""); setNote("");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const openEdit = (d) => {
    setEditing(d);
    setEAmount(String(d.amount));
    setEPaidAt(d.paid_at);
    setEMemo(d.note || d.memo || "");
  };

  const saveEdit = async (ev) => {
    ev.preventDefault();
    if (!editing) return;
    setBusy(true);
    try {
      await apiClient.patch(`/finance/distributions/${editing.id}`, {
        amount: Number(e_amount),
        paid_at: e_paid_at,
        memo: e_memo,
      });
      toast.success("Distribution updated");
      setEditing(null);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const archiveDist = async (d) => {
    try {
      await apiClient.post(`/finance/distributions/${d.id}/${d.archived ? "unarchive" : "archive"}`);
      toast.success(d.archived ? "Restored" : "Archived");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteDist = async (d) => {
    if (!window.confirm(`Delete this distribution to ${d.partner_email} for ${fmtUsd(d.amount)}?`)) return;
    try {
      await apiClient.delete(`/finance/distributions/${d.id}`);
      toast.success("Deleted");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <FinanceLayout>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <PieChart size={20} className="text-nex-gold" />
            <div className="mono-label">// PARTNER SPLITS</div>
          </div>
          <h1 className="serif-head text-3xl">Distributions</h1>
          <p className="text-nex-chrome mt-2 text-sm">
            Every figure is pulled directly from what you booked in Transactions and Expenses — no auto-percentages, no floor. Click any tile to jump to the ledger it&apos;s pulled from.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="dist-refresh"
          className="tick-btn-secondary text-xs !py-2 !px-3"
          title="Refresh totals"
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-center py-20"><Loader2 className="animate-spin text-nex-gold mx-auto" /></div>
      ) : (
        <>
          {/* Liquid Cash = revenue − categorized expenses (the pile). */}
          {/* Effective Net Distributable = Liquid Cash − Allocations.  */}
          <SmartAllocationCard
            liquidCash={summary.net_distributable}
            onAllocationChange={setAllocated}
          />

          {(() => {
            const liquidCash = summary.net_distributable;
            const effectiveNet = liquidCash - allocated.total;
            const perPartner = effectiveNet / Math.max(1, (summary.partners || []).length);
            return (
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-8" data-testid="dist-summary">
                <Metric
                  label="Revenue"
                  value={fmtUsd(summary.revenue)}
                  onClick={() => navigate("/admin/finance/transactions?direction=in")}
                  testid="dist-metric-revenue"
                  hint="From Income transactions"
                />
                <Metric
                  label="COGS"
                  value={fmtUsd(summary.cogs)}
                  muted
                  onClick={() => navigate("/admin/finance/expenses?category=cogs")}
                  testid="dist-metric-cogs"
                  hint="Expenses · category COGS"
                />
                <Metric
                  label="Reserve"
                  value={fmtUsd(summary.reserve)}
                  muted
                  onClick={() => navigate("/admin/finance/expenses?category=reserve")}
                  testid="dist-metric-reserve"
                  hint="Expenses · category Reserve"
                />
                <Metric
                  label="OP Expenses"
                  value={fmtUsd(summary.op_expenses)}
                  muted
                  onClick={() => navigate("/admin/finance/expenses?category=operating")}
                  testid="dist-metric-op"
                  hint="Expenses · category Operating"
                />
                <Metric
                  label="Net Distributable"
                  value={fmtUsd(effectiveNet)}
                  accent={effectiveNet < 0 ? "red" : "gold"}
                  testid="dist-metric-net"
                  hint={`Liquid Cash ${fmtUsd(liquidCash)} − Alloc ${fmtUsd(allocated.total)}`}
                />
              </div>
            );
          })()}

          <div className="card-industrial corner-tick overflow-hidden mb-8" data-testid="partners-table">
            {(() => {
              const liquidCash = summary.net_distributable;
              const effectiveNet = liquidCash - allocated.total;
              const partnerCount = Math.max(1, (summary.partners || []).length);
              const perPartner = effectiveNet / partnerCount;
              return (
                <div className="p-5 border-b border-nex-border">
                  <div className="mono-label mb-1">// PER-PARTNER SHARE</div>
                  <h2 className={`serif-head text-xl ${effectiveNet < 0 ? "text-red-300" : ""}`}>
                    {summary.year} · {fmtUsd(perPartner)} each
                  </h2>
                  <p className="mono-body text-[10px] uppercase tracking-widest text-nex-muted mt-1">
                    After allocation of {fmtUsd(allocated.total)} to Operating + Growth + Reserve
                  </p>
                </div>
              );
            })()}
            <table className="w-full text-sm">
              <thead className="bg-black/30 border-b border-nex-border">
                <tr className="mono-body text-[10px] uppercase tracking-[0.15em] text-nex-chrome">
                  <th className="text-left px-4 py-3">Partner</th>
                  <th className="text-right px-4 py-3">Share</th>
                  <th className="text-right px-4 py-3">Paid YTD</th>
                  <th className="text-right px-4 py-3">Remaining</th>
                </tr>
              </thead>
              <tbody>
                {summary.partners.map((p) => {
                  const liquidCash = summary.net_distributable;
                  const effectiveNet = liquidCash - allocated.total;
                  const partnerCount = Math.max(1, summary.partners.length);
                  const share = effectiveNet / partnerCount;
                  const remaining = share - (p.paid || 0);
                  return (
                    <tr key={p.email} className="border-b border-nex-border/50" data-testid={`partner-row-${p.email}`}>
                      <td className="px-4 py-3">
                        <div className="text-nex-silver">{p.name}</div>
                        <div className="mono-body text-[10px] text-nex-muted">{p.email}</div>
                      </td>
                      <td className={`px-4 py-3 text-right ${share < 0 ? "text-red-300" : "text-nex-silver"}`}>{fmtUsd(share)}</td>
                      <td className="px-4 py-3 text-right text-nex-chrome">{fmtUsd(p.paid)}</td>
                      <td className={`px-4 py-3 text-right font-medium ${remaining < 0 ? "text-red-300" : remaining > 0 ? "text-nex-gold" : "text-emerald-300"}`}>
                        {fmtUsd(remaining)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Record paid — partner-only */}
          {user?.is_partner ? (
            <form onSubmit={recordPaid} className="card-industrial corner-tick p-5" data-testid="record-paid-form">
              <div className="flex items-center gap-2 mb-4">
                <DollarSign size={16} className="text-nex-gold" />
                <div className="mono-label">// RECORD DISTRIBUTION PAID</div>
              </div>
              <div className="grid md:grid-cols-5 gap-3">
                <select value={selectedPartner} onChange={(e) => setSelectedPartner(e.target.value)} className="input-industrial" data-testid="paid-partner">
                  {summary.partners.map((p) => <option key={p.email} value={p.email}>{p.name}</option>)}
                </select>
                <input type="number" step="0.01" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} placeholder="Amount" className="input-industrial" data-testid="paid-amount" />
                <input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} className="input-industrial" data-testid="paid-date" />
                <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className="input-industrial" data-testid="paid-note" />
                <button type="submit" disabled={busy} className="tick-btn-primary justify-center" data-testid="paid-submit">
                  {busy ? <Loader2 size={13} className="animate-spin" /> : <><Plus size={13} /> Record</>}
                </button>
              </div>
            </form>
          ) : (
            <div className="card-industrial corner-tick p-5 text-nex-chrome text-sm text-center">
              Only Partners can record distributions.
            </div>
          )}

          {/* HISTORY */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="mono-label">// DISTRIBUTION HISTORY</div>
              {user?.is_partner && (
                <label className="flex items-center gap-2 text-nex-chrome text-xs cursor-pointer select-none">
                  <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="dist-show-archived" className="accent-nex-gold" />
                  Show archived
                </label>
              )}
            </div>
            <div className="card-industrial corner-tick overflow-hidden" data-testid="dist-history">
              {history.length === 0 ? (
                <div className="text-center py-10 text-nex-chrome text-sm">No distributions recorded yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-black/30 border-b border-nex-border">
                      <tr className="mono-body text-[10px] uppercase tracking-[0.15em] text-nex-chrome">
                        <th className="text-left px-4 py-3">Date</th>
                        <th className="text-left px-4 py-3">Partner</th>
                        <th className="text-right px-4 py-3">Amount</th>
                        <th className="text-left px-4 py-3">Note</th>
                        <th className="text-right px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((d) => (
                        <tr key={d.id} className={`border-b border-nex-border/50 hover:bg-white/[0.02] ${d.archived ? "opacity-50" : ""}`} data-testid={`dist-row-${d.id}`}>
                          <td className="px-4 py-3 mono-body text-[11px] text-nex-chrome">{d.paid_at}</td>
                          <td className="px-4 py-3 text-nex-silver">
                            {d.partner_email}
                            {d.archived && <span className="ml-2 mono-body text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-nex-border/40 text-nex-muted">Archived</span>}
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-nex-silver">{fmtUsd(d.amount)}</td>
                          <td className="px-4 py-3 text-nex-muted text-[12px]">{d.note || d.memo}</td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            {user?.is_partner && (
                              <>
                                <button onClick={() => openEdit(d)} className="text-nex-chrome hover:text-nex-gold mr-3" data-testid={`edit-dist-${d.id}`} title="Edit"><Pencil size={14} /></button>
                                <button onClick={() => archiveDist(d)} className="text-nex-chrome hover:text-nex-gold mr-3" data-testid={`archive-dist-${d.id}`} title={d.archived ? "Restore" : "Archive"}>
                                  {d.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                                </button>
                                <button onClick={() => deleteDist(d)} className="text-nex-muted hover:text-red-300" data-testid={`delete-dist-${d.id}`} title="Delete"><Trash2 size={14} /></button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {editing && (
        <>
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[75]" onClick={() => setEditing(null)} />
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[80] w-full max-w-md px-4" data-testid="edit-dist-modal">
            <div className="card-industrial corner-tick p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="serif-head text-xl">Edit Distribution</h3>
                <button onClick={() => setEditing(null)} className="text-nex-muted hover:text-nex-silver" data-testid="edit-dist-close"><X size={18} /></button>
              </div>
              <form onSubmit={saveEdit} className="space-y-3">
                <label className="block">
                  <div className="mono-body text-[10px] uppercase tracking-widest text-nex-chrome mb-1">Amount ($)</div>
                  <input type="number" step="0.01" value={e_amount} onChange={(ev) => setEAmount(ev.target.value)} className="input-industrial w-full" data-testid="edit-dist-amount" />
                </label>
                <label className="block">
                  <div className="mono-body text-[10px] uppercase tracking-widest text-nex-chrome mb-1">Paid on</div>
                  <input type="date" value={e_paid_at} onChange={(ev) => setEPaidAt(ev.target.value)} className="input-industrial w-full" data-testid="edit-dist-date" />
                </label>
                <label className="block">
                  <div className="mono-body text-[10px] uppercase tracking-widest text-nex-chrome mb-1">Note</div>
                  <input type="text" value={e_memo} onChange={(ev) => setEMemo(ev.target.value)} className="input-industrial w-full" data-testid="edit-dist-memo" />
                </label>
                <button type="submit" disabled={busy} className="tick-btn-primary w-full justify-center" data-testid="edit-dist-save">
                  {busy ? <Loader2 size={13} className="animate-spin" /> : "Save changes"}
                </button>
              </form>
            </div>
          </div>
        </>
      )}
    </FinanceLayout>
  );
};

const Metric = ({ label, value, muted, accent, onClick, testid, hint }) => {
  const colorClass = accent === "gold"
    ? "text-nex-gold"
    : accent === "red"
      ? "text-red-300"
      : muted ? "text-nex-chrome" : "text-nex-silver";
  const clickable = typeof onClick === "function";
  return (
    <div
      className={`card-industrial p-4 ${clickable ? "cursor-pointer hover:border-nex-gold/60 hover:bg-white/[0.02] transition-colors" : ""}`}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") onClick(); } : undefined}
      data-testid={testid}
    >
      <div className="mono-label !text-[9px] mb-2">{label}</div>
      <div className={`serif-head text-2xl ${colorClass}`}>{value}</div>
      {hint && <div className="mono-body text-[10px] text-nex-muted mt-1.5">{hint}</div>}
    </div>
  );
};

export default DistributionsPage;
